// Claude.ai 룩앤필 디자인 토큰 (전역)
// v2 디자인 톤을 사이트 전체에서 사용하기 위한 단일 진실의 출처(SSOT).
// 기존 styles/theme.ts 의 designSystem / MUI palette 모두 이 토큰을 참조한다.

export const claudeTokens = {
    color: {
        // 기본 — 이미지 톤 (cool soft mint/white, 의료/웰니스 베이스)
        bg: '#F5F8F7',
        surface: '#FFFFFF',
        bgSunken: '#EAF0EE',       // 입력칸/disabled 등 가라앉은 배경
        // 액센트 — 차분한 틸/세이지 그린 (의료/웰니스 톤)
        accent: '#5A9B96',
        accentDark: '#3D7E79',
        accentSoft: '#D4E8E5',     // tint, hover 배경, 선택 상태
        // 텍스트
        // UI 가이드 1장 회색 계열과 같은 값(2026-10-06, 이전 베이지 #1F1B16·#6B5B4D·#7D6F62).
        text1: '#1F2A28',
        text2: '#4F5B58',
        text3: '#66736F',          // 흐린 글자 = FORM_COLOR.muted. 대비 흰 바탕 4.94·앱 바탕 #F5F8F7 4.63 (AA 4.5 충족 — 2026-10-06 사용자 결정, 시안 #7A8784 는 3.73 미달)
        // 보더
        border: '#E3E8E6',         // 구분선 = FORM_COLOR.line (이전 #E8E0D5)
        borderStrong: '#D3DBD8',   // 입력 focus 전 등 강조 보더 = FORM_COLOR.border (이전 #D4C7B5)
        // 상태
        status: {
            success: '#2E7D32',    // chip.management fg와 통일
            warning: '#9B7A1A',    // chip.question fg와 통일
            error: '#B83A2E',      // coral 계열 deep red — 위험 신호 유지
            info: '#3B5BDB',       // chip.clinical fg와 통일
        },
        // 카테고리 칩 (기존 유지)
        chip: {
            clinical: { bg: '#E8EEFF', fg: '#3B5BDB' },
            management: { bg: '#E8F5E9', fg: '#2E7D32' },
            etc: { bg: '#F0EDE7', fg: '#5A4F3F' },
            question: { bg: '#FDF3D4', fg: '#9B7A1A' },
            column: { bg: '#E8EEFF', fg: '#3B5BDB' },
            news: { bg: '#E8F5E9', fg: '#2E7D32' },
            paper: { bg: '#FCE7F3', fg: '#A8307E' },
        },
    },
    radius: { sm: '6px', md: '10px', lg: '16px' },
    shadow: {
        // 카드 정적 상태: 그림자 없음 (border 만으로 경계 표현)
        card: 'none',
        // hover 시에만 약하게
        hover: '0 2px 8px rgba(0,0,0,0.04)',
    },
    font: {
        heading: "'Pretendard Variable', Pretendard, system-ui, sans-serif",
        body: "'Pretendard Variable', Pretendard, system-ui, sans-serif",
    },
    space: {
        xs: '4px',
        sm: '8px',
        md: '16px',
        lg: '24px',
        xl: '40px',
        xxl: '64px',
    },
} as const;

// chip 매핑 헬퍼: 카테고리 코드 → 색상 토큰
export const getChipTone = (code?: string) => {
    const c = (code || '').toUpperCase();
    if (c.includes('CLINIC')) return claudeTokens.color.chip.clinical;
    if (c.includes('MANAG')) return claudeTokens.color.chip.management;
    if (c.includes('QUEST') || c.includes('QNA')) return claudeTokens.color.chip.question;
    if (c.includes('COLUMN')) return claudeTokens.color.chip.column;
    if (c.includes('NEWS')) return claudeTokens.color.chip.news;
    if (c.includes('PAPER')) return claudeTokens.color.chip.paper;
    return claudeTokens.color.chip.etc;
};

// contentType 라벨/아이콘 매핑용 (아이콘은 컴포넌트에서 해석)
export type ExpertContentTypeKey = 'QUESTION' | 'COLUMN' | 'NEWS' | 'PAPER' | 'OTHER';

export const resolveContentTypeKey = (type?: string): ExpertContentTypeKey => {
    const t = (type || '').toUpperCase();
    if (t.includes('QUEST') || t.includes('QNA')) return 'QUESTION';
    if (t.includes('COLUMN')) return 'COLUMN';
    if (t.includes('NEWS')) return 'NEWS';
    if (t.includes('PAPER')) return 'PAPER';
    return 'OTHER';
};
